import { useMemo, useEffect, useRef } from 'react';

import {
  InfiniteData,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { toast } from 'sonner';

import { broadcastsEndpoints as endpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import {
  ApiStatusFilter,
  useBroadcastFilter,
  type BroadcastSortDirection,
} from '@/modules/broadcast/contexts/BroadcastFilterContext';
import { useWhatsAppTemplates } from '@/modules/whatsapp/hooks/useWhatsAppTemplates';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountId } from '@/redux/session/selectors';
import { Broadcast, Broadcasts, PartialBroadcast } from '@/shared/types/broadcast';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { selectBroadcast, selectBroadcastDeskId } from '../redux/selectors';

const TWELVE_SECONDS = 12_000;

export const API = Object.freeze({
  listBroadcasts: async (
    pageParam?: { cursor?: string },
    filter?: string,
    status?: ApiStatusFilter,
    sortDirection?: BroadcastSortDirection
  ): Promise<Broadcasts> =>
    callGet(endpoints.broadcasts(pageParam?.cursor, filter, status, sortDirection)),

  getBroadcast: async (broadcastId: string): Promise<Broadcast> =>
    callGet(endpoints.broadcast(broadcastId)),

  createBroadcast: async (newBroadcast: Partial<Broadcast>): Promise<Broadcast> =>
    callPost(endpoints.broadcasts(), newBroadcast),

  updateBroadcast: async ({ broadcast_id, ...broadcast }: PartialBroadcast): Promise<Broadcast> =>
    callPut(endpoints.broadcast(broadcast_id), broadcast),

  deleteBroadcast: async (broadcastId: string, force?: boolean): Promise<Broadcast> => {
    const queryParams = force ? `?force=${force}` : '';
    return callDelete(`${endpoints.broadcast(broadcastId)}${queryParams}`);
  },
});

interface DeleteProps {
  broadcast_id: string;
  force?: boolean;
}

export const onBroadcastUpdated = (
  queryClient: QueryClient,
  broadcast: Broadcast,
  filter?: string,
  apiStatus?: ApiStatusFilter,
  sortDirection?: BroadcastSortDirection
) => {
  // Update individual broadcast cache
  queryClient.setQueryData<Broadcast>(
    [endpoints.broadcast(broadcast.broadcast_id)],
    (prev: Broadcast) => ({ ...prev, ...broadcast })
  );

  // Update function to apply to all matching caches
  const updateBroadcastInCache = (prev: InfiniteData<Broadcasts>) => {
    if (!prev) {
      return prev;
    }

    return {
      ...prev,
      pages: prev.pages.map((page) => ({
        ...page,
        broadcasts: page.broadcasts.map((acc) =>
          acc.broadcast_id === broadcast.broadcast_id ? broadcast : acc
        ),
      })),
    };
  };

  // 1. update the current view's cache
  const currentQueryKey = [
    endpoints.broadcasts,
    filter,
    broadcast.account_id,
    apiStatus,
    sortDirection,
  ];

  if (queryClient.getQueryData(currentQueryKey)) {
    queryClient.setQueryData(currentQueryKey, updateBroadcastInCache);
  }

  // 2. find and update all other broadcast queries for this account
  const queryCache = queryClient.getQueryCache();
  queryCache
    .findAll({
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === endpoints.broadcasts &&
          key[2] === broadcast.account_id &&
          key.toString() !== currentQueryKey.toString()
        ); // not the one we just updated
      },
    })
    .forEach((query) => {
      queryClient.setQueryData(query.queryKey, updateBroadcastInCache);
    });
};

export const onBroadcastCreated = (
  queryClient: QueryClient,
  broadcast: Broadcast,
  filter?: string,
  apiStatus?: ApiStatusFilter,
  sortDirection?: BroadcastSortDirection
) => {
  const addBroadcastToCache = (prev: InfiniteData<Broadcasts> | undefined) => {
    if (!prev) {
      return prev;
    }

    const newPages = prev.pages.map((page, index) => {
      if (index === 0) {
        return {
          ...page,
          pagination: { ...page.pagination, total: (page.pagination.total || 0) + 1 },
          broadcasts: [
            broadcast,
            ...page.broadcasts.filter((acc) => acc.broadcast_id !== broadcast.broadcast_id),
          ],
        };
      }
      return page;
    });

    return { ...prev, pages: newPages };
  };

  // 1. update the current view's cache
  const currentQueryKey = [
    endpoints.broadcasts,
    filter || '',
    broadcast.account_id,
    apiStatus,
    sortDirection,
  ];

  // Check and update current cache
  const currentCache = queryClient.getQueryData(currentQueryKey);
  if (currentCache) {
    queryClient.setQueryData(currentQueryKey, addBroadcastToCache);
  }

  // 2. find and update all other broadcast queries for this account
  const queryCache = queryClient.getQueryCache();
  const otherQueries = queryCache.findAll({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === endpoints.broadcasts &&
        key[2] === broadcast.account_id &&
        JSON.stringify(key) !== JSON.stringify(currentQueryKey)
      ); // not the one we just updated
    },
  });

  otherQueries.forEach((query) => {
    queryClient.setQueryData(query.queryKey, addBroadcastToCache);
  });
};

export const onBroadcastRemoved = (
  queryClient: QueryClient,
  account_id: string,
  broadcast_id: string,
  filter?: string,
  apiStatus?: ApiStatusFilter,
  sortDirection?: BroadcastSortDirection
) => {
  // Update function to apply to all matching caches
  const removeBroadcastFromCache = (prev: InfiniteData<Broadcasts>) => {
    if (!prev) {
      return prev;
    }

    return {
      ...prev,
      pages: prev.pages.map((page, index) => {
        if (index === 0) {
          return {
            ...page,
            pagination: {
              ...page.pagination,
              total: Math.max(0, (page.pagination.total || 0) - 1),
            },
            broadcasts: page.broadcasts.filter((acc) => acc.broadcast_id !== broadcast_id),
          };
        }
        return {
          ...page,
          broadcasts: page.broadcasts.filter((acc) => acc.broadcast_id !== broadcast_id),
        };
      }),
    };
  };

  // First update the current view's cache
  const currentQueryKey = [endpoints.broadcasts, filter, account_id, apiStatus, sortDirection];

  if (queryClient.getQueryData(currentQueryKey)) {
    queryClient.setQueryData(currentQueryKey, removeBroadcastFromCache);
  }

  // Then find and update all other broadcast queries for this account
  const queryCache = queryClient.getQueryCache();
  queryCache
    .findAll({
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === endpoints.broadcasts &&
          key[2] === account_id && // account_id match
          key.toString() !== currentQueryKey.toString()
        ); // not the one we just updated
      },
    })
    .forEach((query) => {
      queryClient.setQueryData(query.queryKey, removeBroadcastFromCache);
    });

  // Remove individual broadcast query
  queryClient.removeQueries({ queryKey: [endpoints.broadcast(broadcast_id)] });
};

interface UseBroadcastsProps {
  broadcastId?: string;
  desk_id?: string;
  integration_id?: string;
}

export const useBroadcasts = ({
  broadcastId,
  desk_id,
  integration_id,
}: UseBroadcastsProps = {}) => {
  const { apiStatus, sortDirection, deferredTextFilter: filter } = useBroadcastFilter();
  const queryClient = useQueryClient();
  const accountId = useSelector(selectAccountId);
  const broadcastDeskId = useSelector(selectBroadcastDeskId);
  const draftBroadcast = useSelector(selectBroadcast);
  const location = useLocation();

  const { t } = useTranslation();
  const canRead = useSelector((state: RootState) =>
    getPermissions(state, 'broadcasts', actions.READ)
  );
  const isOnBroadcastsPage = location.pathname.includes('/broadcasts');

  const broadcastsQueryKey = useMemo(
    () => [endpoints.broadcasts, filter, accountId, apiStatus, sortDirection],
    [filter, accountId, apiStatus, sortDirection]
  );

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const {
    data: broadcasts,
    status: listStatus,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: broadcastsQueryKey,
    queryFn: async ({ pageParam }) => {
      return API.listBroadcasts(pageParam, filter, apiStatus, sortDirection);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.has_more) {
        return { cursor: lastPage.pagination.next_cursor };
      }
      return undefined;
    },
    placeholderData: keepPreviousData,
    initialPageParam: {} as { cursor?: string },
    enabled: !!accountId && canRead,
    staleTime: 10_000,
  });

  // Single polling effect: every 12 seconds, check for any broadcast with a 'ready' status and refetch data only if needed.
  // This avoids redundant polling across multiple cached pages from useInfiniteQuery.
  useEffect(() => {
    // Only poll list on actual list page or when fetching draft broadcasts
    const shouldPollList = isOnBroadcastsPage && (!broadcastId || broadcastId === 'draft');

    if (!shouldPollList || !accountId || !canRead) {
      return;
    }

    const poll = async () => {
      const hasReadyBroadcast = broadcasts?.pages.some((page) =>
        page.broadcasts.some((broadcast) => broadcast.status === 'ready')
      );

      if (hasReadyBroadcast) {
        await refetch();
      }

      timerRef.current = setTimeout(poll, TWELVE_SECONDS);
    };

    timerRef.current = setTimeout(poll, TWELVE_SECONDS);

    return () => {
      clearTimeout(timerRef.current!);
    };
  }, [broadcasts?.pages, refetch, isOnBroadcastsPage, accountId, canRead, broadcastId]);

  const {
    data: broadcast,
    status: getStatus,
    isLoading: isBroadcastLoading,
  } = useQuery<Broadcast, Error>({
    queryKey: [endpoints.broadcast(broadcastId)],
    queryFn: () => API.getBroadcast(broadcastId),
    enabled: !!broadcastId && canRead && broadcastId !== 'draft',
    // Smart conditional polling: only poll when broadcast is actively processing
    refetchInterval: (query) => {
      const currentBroadcast = query.state.data;
      const status = currentBroadcast?.status;
      // Only refetch if broadcast is actively being processed
      if (status === 'in_progress' || status === 'ready') {
        return 5 * 1000; // Keep original 5 seconds for active broadcasts
      }
      // No automatic refetching for completed, sent, or draft broadcasts
      return false;
    },
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
  });

  const integrationId = draftBroadcast?.integration_id || integration_id;
  const deskId = broadcastDeskId || desk_id;

  const { templates: whatsAppTemplates, status: listwhatsAppTemplatesStatus } =
    useWhatsAppTemplates({ deskId, integrationId });

  const {
    mutate: createBroadcast,
    mutateAsync: createBroadcastAsync,
    status: createStatus,
  } = useMutation<Broadcast, Error, Partial<Broadcast>>({
    mutationFn: (broadcast) => API.createBroadcast(broadcast),
    onSuccess: (resp) => {
      onBroadcastCreated(queryClient, resp, filter, apiStatus, sortDirection);
      toast.success(t('broadcasts.broadcast_created', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });

  const {
    mutate: updateBroadcast,
    mutateAsync: updateBroadcastAsync,
    status: updateStatus,
  } = useMutation<Broadcast, Error, PartialBroadcast>({
    mutationFn: API.updateBroadcast,
    onSuccess: (resp) => {
      onBroadcastUpdated(queryClient, resp, filter, apiStatus, sortDirection);
    },
    meta: { errorToast: true },
  });
  const { mutate: deleteBroadcast, status: deleteStatus } = useMutation<
    Broadcast,
    Error,
    DeleteProps
  >({
    mutationFn: ({ broadcast_id, force }) => API.deleteBroadcast(broadcast_id, force),
    onSuccess: (resp) => {
      onBroadcastRemoved(
        queryClient,
        accountId,
        resp.broadcast_id,
        filter,
        apiStatus,
        sortDirection
      );
      toast.success(t('broadcasts.broadcast_deleted'));
    },
    meta: { errorToast: true },
  });

  const flatBroadcasts = useMemo(
    () => broadcasts?.pages?.flatMap((page) => page.broadcasts),
    [broadcasts?.pages]
  );

  const totalRows = useMemo(() => broadcasts?.pages[0]?.pagination?.total, [broadcasts?.pages]);

  const broadcastNames = useMemo(
    () => flatBroadcasts?.map((b) => b.name.toLowerCase()),
    [flatBroadcasts]
  );
  return {
    broadcasts: broadcasts?.pages || [],
    flatBroadcasts,
    broadcast,
    getStatus,
    isBroadcastLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    whatsAppTemplates,
    listwhatsAppTemplatesStatus,
    createStatus,
    listStatus,
    updateStatus,
    createBroadcast,
    createBroadcastAsync,
    updateBroadcast,
    updateBroadcastAsync,
    deleteBroadcast,
    deleteStatus,
    broadcastNames,
    totalRows,
    sortDirection,
    showBroadcasts: totalRows > 0,
  };
};
